"use client";

import React from "react";
import { CircleCheck, ArrowRight, Loader, PhoneCall, HeartPulse } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const EXPECTATIONS = ["We will review your loved one's diagnosis in plain, understandable language.", "Explore all viable options including active surveillance, surgery, radiation, focal therapy, and systemic treatments. We help you understand how different paths may impact your quality of life, so you can make decisions aligned with your personal goals.", "We'll help you prepare for upcoming visits with the oncology team.", "You'll learn about genomic testing and what the results mean for your family."];
const CARE_OPTIONS = ["Patient", "Spouse / Partner", "Adult Child", "Other Family Member", "Caregiver", "Friend"];
function CaregiverForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="123691" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Consultation Requested</h3>
        <p className="text-muted-foreground font-sans max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full border-secondary text-secondary hover:bg-secondary/10">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" className="rounded-xl bg-background/50 border-border focus:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" className="rounded-xl bg-background/50 border-border focus:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-()]{7,15}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="rounded-xl bg-background/50 border-border focus:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="care_type" rules={{
        required: "Please select your relationship"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-sans">Relationship to Patient</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="rounded-xl bg-background/50 border-border focus:ring-ring">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CARE_OPTIONS.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-sans">Preferred Consultation Date <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input type="date" className="rounded-xl bg-background/50 border-border focus:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-sans">Specific Questions or Concerns <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="How can I best support you and your family today?" className="rounded-xl resize-none min-h-[100px] bg-background/50 border-border focus:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all group bg-gradient-to-r from-secondary to-secondary/80 text-secondary-foreground border-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : <>
            Request Consultation
            <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function RequestACaregiver() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="connect-with-an-experienced" ref={sectionRef} className="dark relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <div className={`lg:col-span-5 space-y-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/20 text-secondary text-sm font-medium border border-secondary/30">
                <HeartPulse className="size-4" />
                <span className="font-sans">Empathetic Guidance</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground leading-tight">I am here for <span className="text-primary" data-ast-id="f:/src/pages/FamilySupport/sections/RequestACaregiver.tsx|sig:p8yc2l" data-ast-start="9126" data-ast-end="9173">you, too.</span></h2>
              <p className="text-lg font-sans text-muted-foreground leading-relaxed">Supporting a loved one through a prostate cancer diagnosis is a journey of its own. I help families navigate the complexities of care with confidence and clarity.</p>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-serif font-semibold text-foreground flex items-center gap-2">
                <PhoneCall className="size-5 text-secondary" />
                What to expect during our consultation:
              </h3>
              <ul className="space-y-4">
                {EXPECTATIONS.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-6 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground/80 font-sans leading-relaxed">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-6 border-t border-border/50">
              <p className="text-sm font-sans text-muted-foreground italic">Providing families with the personalized support and clinical insight needed to make informed decisions together.</p>
            </div>
          </div>

          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="connect_expert_form_family_support" formId="connect_expert_form_family_support" sectionName="Connect with an Experienced" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            care_type: "",
            preferred_date: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            care_type: {
              required: true
            }
          }} submitText="Request Consultation" successMessage="Your message has been received. I will reach out shortly to schedule a time to speak with you and your family." className="bg-card/40 backdrop-blur-xl rounded-3xl p-6 sm:p-10 shadow-2xl border border-white/10 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
              
              <div className="relative z-10">
                <div className="mb-8">
                  <h3 className="text-3xl font-serif font-bold text-foreground">Connect with an Experienced</h3>
                  <p className="text-muted-foreground font-sans mt-2">Share your details and I will help guide you through the next steps.</p>
                </div>
                <CaregiverForm />
              </div>
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}